import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
  YupBoolean,
  YupObject,
  ButtonNewRecord,
  toastWarning,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import {
  gqlGetByIdQuery,
  gqlUpdate,
  gqlDelete,
  FormInput,
  IdocExtraInput,
  getContainertypes,
  getCollectorById,
  getCollectorIdocSeriesId,
  gqlAdd,
  gqlCanAddIdoc,
} from '../graphql';
import '../../../../styles/tab-styles.css';
import { IdocFormFields } from './idoc-form-fields';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { afisDefaults, IdocWasteOriginTypes, RowFieldTooltips } from 'shared-pages';
import { convertFieldToFloat, convertFieldToInt } from 'shared-pages/common/form-helpfunc';
import { gql, useApolloClient } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import {
  isRebattery,
  requiredString,
  isAfisCyprus,
  isMedicycle,
  isFotokiklosi,
} from 'shared-pages/common/constants';
import { Button, Spinner } from 'react-bootstrap';
import { FaFilePdf } from 'react-icons/fa';
import { startOfDay } from 'date-fns';
import { useTriggerMailService } from 'shared-pages/hooks/trigger-mail-service';
import { hasDateStorage, hasReceptionNetWeight } from './idoc-reception-validation';

// Customize FormShape based on Customer/Company
function useCustomFormShape(base: any) {
  const { t } = useTranslation();
  const reqStr = t(requiredString);

  let formShape = Yup.object(base);

  // // Use concat to overlay stricter rules on top of the existing object schema.
  // formShape = formShape.concat(
  //   Yup.object({
  //     adrNumber: Yup.string().required(reqStr),
  //     adrExpDate: YupDate.required(reqStr),
  //   }),
  // );

  // // Update a field with shape (also immutable)
  // formShape = formShape.shape({
  //   name: Yup.string().notRequired(),
  //   vehicleId: formShape.fields['vehicleId'].required('Vehicle is required'),
  // });

  // ReBattery
  if (isRebattery) {
    formShape = formShape.shape({
      regionId: YupNumeric.notRequired(),
      wastereceptionpointCompanyRegionId: YupNumeric.notRequired(),
    });
  }
  // Φωτοκύκλωση
  else if (isFotokiklosi) {
    // Update a field with shape (also immutable)
    formShape = formShape.shape({
      regionId: YupNumeric.notRequired(),
      colpointVatNumber: YupString.notRequired(),
      wastereceptionpointCompanyRegionId: YupNumeric.notRequired(),
      collectorCompanyTaxOffice: YupString.notRequired(),
      wastereceptionpointCompanyTaxOffice: YupString.notRequired(),
      adrNumber: Yup.string().nullable().notRequired(),
    });
  }
  // Medicycle
  else if (isMedicycle || isAfisCyprus) {
    // Update a field with shape (also immutable)
    formShape = formShape.shape({
      ekaCode: YupString.notRequired(),
      collectorCompanyVat: YupString.notRequired(),
      collectorCompanyTaxOffice: YupString.notRequired(),

      regionId: YupNumeric.notRequired(),
      subregionId: YupNumeric.notRequired(),
      adrNumber: Yup.string().nullable().notRequired(),
      colpointVatNumber: YupString.notRequired(),
      colpointTaxOffice: YupString.notRequired(),

      wastereceptionpointCompanyRegionId: YupNumeric.notRequired(),
      wastereceptionpointCompanySubregionId: YupNumeric.notRequired(),
      wastereceptionpointCompanyTaxOffice: YupString.notRequired(),
      wastereceptionpointCompanyVat: YupString.notRequired(),
    });
  }

  return formShape;
}

/** Page Component
 */
export const IdocFormPage = () => {
  const { handleGraphqlError } = useHandleError('collector');
  const { appStore, authStore } = useRootStore();
  const history = useHistory();
  const location: any = useLocation();
  const { id } = useParams<{ id: string }>();
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const canDelete = isNew ? false : appStore.isPlatformOwner || appStore.isCollector ? true : false;
  const { t, i18n } = useTranslation();
  const client = useApolloClient();
  const { triggerMailService } = useTriggerMailService();

  const formShape = {
    idocMultiId: YupNumeric,
    isMulti: YupBoolean,
    isCancelled: YupBoolean,
    idocseriesId: YupNumeric.required(t(requiredString)),
    identificationDocument: YupString,
    idocPrefix: YupString,
    idocNumber: YupNumeric,

    dateCollected: YupString.required(t(requiredString)),
    colpointContactPerson: YupString,
    netWeight: YupNumeric,
    grossWeight: YupNumeric.when('isCancelled', {
      is: (val) => !val,
      then: (schema) =>
        schema
          .min(0, t('Το Μικτό βάρος πρέπει να είναι ίσο ή μεγαλύτερο του μηδενός'))
          .required(t(requiredString)),
    }),
    tareWeight: YupNumeric.min(0, 'Το Απόβαρο πρέπει να είναι ίσο ή μεγαλύτερο του μηδενός'),
    'wasteorigintype.name': YupString,
    wasteOriginTypeId: YupNumeric.when('isCancelled', {
      is: (val) => !val,
      then: YupNumeric.required(t(requiredString)),
    }),
    materialId: YupNumeric.when('isCancelled', {
      is: (val) => !val,
      then: YupNumeric.required(t(requiredString)),
      otherwise: YupNumeric,
    }),
    ekaCode: YupString.when('isCancelled', {
      is: (val) => !val,
      then: YupString.required(t(requiredString)),
      otherwise: YupString,
    }),
    unClass: YupString,
    collectionPointId: YupNumeric.when(['isCancelled', 'wasteOriginTypeId'], {
      is: (isCancelled, wasteOriginTypeId) =>
        !isCancelled && (isRebattery || wasteOriginTypeId === IdocWasteOriginTypes.PrimaryStorage),
      then: YupNumeric.required(t(requiredString)),
    }),
    fromWastereceptionpointId: YupNumeric.when(['isCancelled', 'wasteOriginTypeId'], {
      is: (isCancelled, wasteOriginTypeId) =>
        !isRebattery && !isCancelled && wasteOriginTypeId === IdocWasteOriginTypes.SecondaryStorage,
      then: YupNumeric.required(t(requiredString)),
    }),
    regionId: YupNumeric.when('isCancelled', {
      is: (val) => !val,
      then: YupNumeric.required(t(requiredString)),
    }),
    subregionId: YupNumeric.when('isCancelled', {
      is: (val) => !val,
      then: YupNumeric.required(t(requiredString)),
    }),
    rbGeoAreaId: YupNumeric,
    collectionPointName: YupString.when('isCancelled', {
      is: (val) => !val,
      then: YupString.required(t(requiredString)),
    }),
    colpointAddressLine1: YupString.when('isCancelled', {
      is: (val) => !val,
      then: YupString.required(t(requiredString)),
    }),
    colpointAddressLine2: YupString,
    colpointAddressArea: YupString,
    colpointAddressCity: YupString,
    colpointPostalCode: YupString,
    colpointVatNumber: !isAfisCyprus
      ? YupString.when('isCancelled', {
          is: false,
          then: (schema) => schema.required(t(requiredString)),
        })
      : YupString,
    colpointTaxOffice: !isAfisCyprus
      ? YupString.when('isCancelled', {
          is: false,
          then: (schema) => schema.required(t(requiredString)),
        })
      : YupString,
    colpointImaCode: YupString,
    colpointPhoneNumber: YupString,
    colpointCategoryId: YupNumeric,

    /* Collector */
    collectorId: YupNumeric.when('isCancelled', {
      is: (val) => !val,
      then: YupNumeric.required(t(requiredString)),
    }),
    collectorCompanyName: YupString.when('isCancelled', {
      is: (val) => !val,
      then: YupString.required(t(requiredString)),
    }),
    collectorCompanyAddress: YupString.when('isCancelled', {
      is: (val) => !val,
      then: YupString.required(t(requiredString)),
    }),
    collectorCompanyPhoneNumber: YupString.when('isCancelled', {
      is: (val) => !val,
      then: YupString.required(t(requiredString)),
    }),
    collectorCompanyVat: YupString.when('isCancelled', {
      is: (val) => !val,
      then: YupString.required(t(requiredString)),
    }),
    collectorCompanyTaxOffice: !isAfisCyprus
      ? YupString.when('isCancelled', {
          is: false,
          then: (schema) => schema.required(t(requiredString)),
        })
      : YupString,
    collectorCompanyImaCode: YupString,

    driverId: YupNumeric.when('isCancelled', {
      is: (val) => !val,
      then: YupNumeric.required(t(requiredString)),
    }),
    driverFullName: YupString.when('isCancelled', {
      is: (val) => !val,
      then: YupString.required(t(requiredString)),
    }),
    driverSignFileId: YupNumeric,
    vehicleId: YupNumeric.when('isCancelled', {
      is: (val) => !val,
      then: YupNumeric.required(t(requiredString)),
    }),
    vehPlateNumber: YupString,
    adrNumber: YupString.when('isCancelled', {
      is: (val) => !val,
      then: YupString.required(t(requiredString)),
    }),
    altRouteDescription: YupString,

    /* Reception — ημερομηνία παράδοσης και βάρος φορτίου: και τα δύο ή κανένα (.test αντί για αμοιβαίο .when) */
    dateStorage: YupDate.test(
      'dateStorage-paired-with-weight',
      t(requiredString),
      function (value) {
        const { isCancelled, receptionNetWeight } = this.parent;
        if (isCancelled || !hasReceptionNetWeight(receptionNetWeight)) return true;
        return hasDateStorage(value);
      },
    ),
    wasteDestinationTypeId: YupNumeric.when('isCancelled', {
      is: (val) => !val,
      then: YupNumeric.required(t(requiredString)),
    }),
    'wastedestinationtype.name': YupString,
    wastereceptionpointId: YupNumeric.when('isCancelled', {
      is: (val) => !val,
      then: YupNumeric.required(t(requiredString)),
    }),
    wastereceptionpointCompanyName: YupString.when('isCancelled', {
      is: (val) => !val,
      then: YupString.required(t(requiredString)),
    }),
    wastereceptionpointCompanyAddress: YupString.when('isCancelled', {
      is: (val) => !val,
      then: YupString.required(t(requiredString)),
    }),
    wastereceptionpointCompanyPhoneNumber: YupString,
    wastereceptionpointCompanyVat: YupString,
    wastereceptionpointCompanyTaxOffice: !isAfisCyprus
      ? YupString.when('isCancelled', {
          is: false,
          then: (schema) => schema.required(t(requiredString)),
        })
      : YupString,
    wastereceptionpointCompanyImaCode: YupString,
    wastereceptionpointCompanyEmail: YupString,
    wastereceptionpointCompanySubregionId: YupNumeric.when('isCancelled', {
      is: (val) => !val,
      then: YupNumeric.required(t(requiredString)),
    }),
    wastereceptionpointNoteDocNumber: YupString,
    receptionPersonName: YupString,
    receptionNetWeight: YupNumeric.test(
      'receptionNetWeight-paired-with-date',
      t(requiredString),
      function (value) {
        const { isCancelled, dateStorage } = this.parent;
        if (isCancelled || !hasDateStorage(dateStorage)) return true;
        return hasReceptionNetWeight(value);
      },
    ),
    // ====== Section 4: Τελικός Αποδέκτης
    finaldestinationpointId: YupNumeric,
    finaldestinationCompanyName: YupString,
    finaldestinationCompanyAddress: YupString,
    finaldestinationCompanyPhoneNumber: YupString,
    finaldestinationCompanyVat: YupString,
    finaldestinationCompanyTaxOffice: YupString,
    finaldestinationCompanyImaCode: YupString,
    finaldestinationCompanyEmail: YupString,
    finaldestinationCompanyRegionId: YupNumeric,
    finaldestinationCompanySubregionId: YupNumeric,
    finaldestinationCompanyNoteDocNumber: YupString,
    finalDestinationPersonName: YupString,
    finalDestinationSignFileId: YupNumeric,
    finalDestinationDate: YupDate,
    finalDestinationNetWeight: YupNumeric,
    finalDestinationItransId: YupNumeric,
    finalDestinationITrans: YupObject.nullable(),
    // ==================== ΠΡΟΣΘΕΤΑ ΣΤΟΙΧΕΙΑ ΣΥΣΚΕΥΑΣΙΩΝ
    idocExtras: Yup.array().of(
      Yup.object().shape({
        id: YupNumeric,
        containertypeId: YupNumeric,
        containerTypeName: YupString.max(256).when('containertypeId', {
          is: (containertypeId) => containertypeId == null,
          then: (schema) => schema.required(t(requiredString)),
        }),
        containersQty: YupNumeric,
        containerTareWeight: YupNumeric.min(
          0,
          'Το Απόβαρο Συσκευασίας πρέπει να είναι ίσο ή μεγαλύτερο του μηδενός',
        ),
      }),
    ),
    // Ask if we need this field
    collectionId: YupNumeric.nullable(),
    collectionCode: YupString.nullable(),
    iTransLineId: YupNumeric.nullable(),
    iTransLine: YupObject.nullable(),
    transferredWithITransId: YupNumeric.nullable(),
    transferredWithITrans: YupObject.nullable(),
  };

  // const validationSchema = Yup.object().shape(formShape);
  const validationSchema = useCustomFormShape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  const [addNewRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [get, { data: rawData, error, refetch, loading }] = useLazyQuery(gqlGetByIdQuery, {
    fetchPolicy: 'network-only',
  });
  //const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const [data, setData] = useState<any>();
  const [initialNewRecData, setInitialNewRecData] = useState<any>();

  const fetchData = useCallback(async () => {
    const [value, containertypes] = await Promise.all([
      get({ variables: { id: idNum } }),
      getContainertypes(client, { isActive: true }),
    ]);
    const rawData = value.data[Object.keys(value.data)[0]];
    const savedExtras = (rawData.idocExtras ?? []).filter(Boolean);
    const savedContainertypeIds = new Set(
      savedExtras
        .map((extra) => extra.containertypeId)
        .filter((containertypeId) => containertypeId != null),
    );
    // Το GraphQL επιστρέφει containertypeName· η φόρμα κρατά το ελεύθερο όνομα στο containerTypeName
    const normalizedSavedExtras = savedExtras.map((extra) =>
      extra.containertypeId == null
        ? {
            ...extra,
            containerTypeName: extra.containertypeName ?? extra.containerTypeName ?? null,
          }
        : { ...extra, containerTypeName: null },
    );
    const missingExtras = (containertypes?.data ?? [])
      .filter((containertype) => !savedContainertypeIds.has(containertype.id))
      .map((containertype) => ({
        id: null,
        idocId: idNum,
        containertypeId: containertype.id,
        containerTypeName: null,
        containertype: { id: containertype.id, name: containertype.name },
        containersQty: null,
        containerTareWeight: null,
      }));

    // collectionCode το θελουμε για να το δείξει το collection dialog - FormFieldCollectionDlg
    setData({
      ...rawData,
      collectionCode: rawData.collection?.code,
      idocExtras: [...normalizedSavedExtras, ...missingExtras],
    });
  }, [client, get, idNum]);
  /*
       Fetch data or Initialize new record
  */
  useEffect(() => {
    if (isNew) {
      // initialize new record
      initializeNewRecord();
    } else {
      fetchData();
    }
  }, [fetchData, isNew]);

  /*
     Αρχικοποίησε Νέα Εγγραφή
  */
  const initializeNewRecord = async () => {
    console.log('initializeNewRecord');
    // Στο Re-battery / Li-ion το ΕΚΑ δεν προσυμπληρώνεται· έρχεται μόνο όταν επιλεγούν τα χαρακτηριστικά αποβλήτων
    let newRec: any = {
      isCancelled: false,
      wasteOriginTypeId: 1,
    };

    // ΑΦΗΣ ΚΥΠΡΟΣ - Default Values
    if (isAfisCyprus) {
      newRec = {
        ...newRec,
        ...{
          dateCollected: new Date(),
          dateStorage: startOfDay(new Date()),
          materialId: afisDefaults.materialId,
          ekaCode: afisDefaults.ekaCode,
          collectorId: afisDefaults.collectorId,
          wastereceptionpointId: afisDefaults.wastereceptionpointId,
        },
      };
    }

    // Αρχικοποίησε στοιχεία Συλλέκτη αν ο χρήστης είναι συλλέκτης
    if (appStore.isCollector) {
      const collector = await getCollectorById(client, appStore.associatedId);
      // Ο συλλέκτης δεν επιλέγει τον εαυτό του από dropdown, οπότε δεν εκτελείται το
      // onCollectorIdChange και ο Κωδικός ΕΑΕΑ έμενε κενός· τον φέρνουμε από τη σειρά του.
      // Αν η σειρά δεν βρεθεί, το έντυπο πρέπει να ανοίγει κανονικά με κενό κωδικό.
      let idocSeries;
      try {
        idocSeries = await getCollectorIdocSeriesId(client, collector.id);
      } catch (error) {
        console.error('Αποτυχία ανάκτησης σειράς ΕΑΕΑ του συλλέκτη', error);
      }
      newRec = {
        ...newRec,
        collectorId: collector.id,
        collectorCompanyName: collector.name,
        collectorCompanyAddress: collector.addressLine1,
        collectorCompanyPhoneNumber: collector.phone,
        collectorCompanyVat: collector.companyVat,
        collectorCompanyTaxOffice: collector?.companyTaxOffice,
        idocseriesId: idocSeries?.data?.[0]?.id,
        idocPrefix: idocSeries?.data?.[0]?.docPrefix,
      };
    }

    // Αρχικοποίησε τις Συσκευασίες
    const containertypes = await getContainertypes(client, { isActive: true });
    const idocExtras = containertypes.data.map((value) => ({
      id: null,
      idocId: -1,
      containertypeId: value.id,
      containerTypeName: null,
      containertype: { id: value.id, name: value.name },
      containersQty: null,
      containerTareWeight: null,
    }));

    // Νέο Έντυπο απο Συλλογή
    // έχουμε περάσει στο state τις τιμές της συλλογής
    if (location?.state && location.state.collectionId) {
      const s = location.state;
      // Header
      let stateValues: any = {
        collectionId: s.collectionId,
        collectionCode: s.collectionCode,
        collectionPointId: s.collectionPointId,
        collectorId: s.collectorId,
        dateCollected: s.dateCollected,
        dateStorage: s.dateCollected,
        vehicleId: s.vehicleId,
        driverId: s.driverId,
        wastereceptionpointId: s.wastereceptionpointId,
        colpointContactPerson: s.colpointContactPerson,
        finDispNote: s.finDispNote,
      };
      // collection line
      if (s.collectionLine) {
        const line = s.collectionLine;
        stateValues = {
          ...stateValues,
          ...{
            materialId: line.materialId,
            // Gross weight is entered manually; the collection line weight is net.
            grossWeight: undefined,
            tareWeight: 0,
            netWeight: line.netWeight,
            receptionNetWeight: line.netWeight,
          },
        };
        // Βάλε τα τεμάχια στην πρώτη συσκευασία
        if (line.qty && line.qty > 0 && idocExtras[0]) {
          idocExtras[0].containersQty = line.qty;
        }
      }
      newRec = {
        ...newRec,
        ...stateValues,
      };
    }

    setInitialNewRecData({ ...newRec, idocExtras });

    // Auto-populate additional fields by calling the handlers
  };

  // Handler να πάει στην πίσω σελίδα
  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('Idocs')), { ...location.state });
  }, [history, location.state]);

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = async (values: FormValuesType, actions) => {
    actions.setSubmitting(false);

    const mutValues = Lo.pick(values, Lo.keys(new FormInput()));

    convertFieldToFloat(mutValues, [
      'netWeight',
      'grossWeight',
      'tareWeight',
      'receptionNetWeight',
      'finalDestinationNetWeight',
    ]);
    convertFieldToInt(mutValues, ['containersQty', 'idocNumber']);

    // idocExtras
    (mutValues as any).idocExtras = (mutValues as any).idocExtras.map((values) => {
      const newValues = { ...values };
      convertFieldToFloat(newValues, [
        'netWeight',
        'grossWeight',
        'tareWeight',
        'containerTareWeight',
      ]);
      convertFieldToInt(newValues, [
        'id',
        'idocId',
        'containertypeId',
        'materialId',
        'containersQty',
      ]);
      // Το input του API δέχεται containertypeName, όχι το τοπικό πεδίο της φόρμας
      const mutValues = Lo.pick(
        {
          ...newValues,
          containertypeName: newValues.containertypeName ?? newValues.containerTypeName ?? null,
        },
        Lo.keys(new IdocExtraInput()),
      );
      return mutValues;
    });

    // Αν είναι νέα εγγραφή τοτε γράφω όλα τα πεδία
    if (isNew) {
      console.log('[IDoc] Add:', mutValues);
      addNewRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχημένη Προσθήκη Εγγραφής [${d?.data?.addIdoc?.id}]`);
          // Ξαναφόρτωσε το για να το τυπώσουν
          history.push({
            pathname: generatePath(getRouteByName('Idoc'), { id: d?.data?.addIdoc?.id }),
          });
          // Στείλε τα mail που έβαλε το WebApi στο MailRepo
          if (!isMedicycle) {
            try {
              triggerMailService();
            } catch (error) {
              console.log('triggerMailService', error);
            }
          }
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
        });
    } else {
      // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
      // console.log('[Applog] Update: all ', values);
      console.log('[Applog] Update: mut ', mutValues);
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
          // Ξαναφόρτωσε το για να το τυπώσουν
          fetchData();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => {
          toastSuccess(`Επιτυχής Διαγραφή Εγγραφής [${idNum}]`);
          history.goBack();
        })
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  const [devCheckAlarms] = useMutation(gql`
    mutation devCheckAlarms($idocId: Int) {
      devCheckAlarms(idocId: $idocId)
    }
  `);
  async function handleTestCheckAlarm() {
    const res = await devCheckAlarms({ variables: { idocId: idNum } });
  }

  const [canAddIdoc] = useMutation(gqlCanAddIdoc);
  const handleCreateNew = useCallback(async () => {
    const historyPush = () =>
      history.push({
        pathname: generatePath(getRouteByName('Idoc'), { id: -1 }),
        state: {
          pathname: location.pathname,
          ...location.state,
        },
      });

    if (appStore.isCollector) {
      await canAddIdoc({ variables: { collectorId: appStore.associatedId } })
        .then((val) => {
          const canAdd = val.data.canAddIdoc;
          if (canAdd) historyPush();
          else
            toastWarning(
              'Η Άδεια Συλλογής Μεταφοράς σας έχει λήξει. Παρακαλούμε ανεβάστε το νέο έγγραφο άδειας και επικοινωνήστε με την Re-Battery για την απαραίτητη επικύρωση',
            );
        })

        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    } else {
      historyPush();
    }
  }, [
    appStore.associatedId,
    appStore.isCollector,
    canAddIdoc,
    handleGraphqlError,
    history,
    location.pathname,
    location.state,
  ]);

  if (isNew && !initialNewRecData) {
    return <h3 className="text-center">{t('Παρακαλώ Περιμένετε...')}</h3>;
  }

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <>
        <FormCard
          title={
            isNew
              ? t('Νέο Έντυπο Αναγνώρισης')
              : data
              ? `${t('Έντυπο Αναγνώρισης Αποβλήτου')}: ${data?.identificationDocument}`
              : `${t('Έντυπο Αναγνώρισης Αποβλήτου')}: --`
          }
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          onReturn={handleReturn}
          permitSubmit={false}
          permitDelete={canDelete}
          initialValues={isNew ? initialNewRecData : pickProperties(data)}
          validationSchema={validationSchema}
          enableReinitialize
          idx={idNum}
          injectHeaderElements={() => (
            <ButtonNewRecord
              onClick={handleCreateNew}
              size="sm"
              label={t('Νέο Έντυπο Αναγνώρισης')}
            />
          )}
          injectFooterElements={(formikCtx: any) => {
            //
            const { dirty, resetForm, isValid, loading, errors } = formikCtx;

            /*-- Εκτύπωση σε Pdf --*/
            const printPdf = () => {
              // Στο Vite τα env διαβάζονται από import.meta.env (όχι Node process)
              const url = import.meta.env.VITE_APP_WEBAPI.replace(
                '/graphql',
                `/idoc/${id}/${i18n.language}`,
              );
              //window.open(url, '_system', 'location=yes');
              window.open(url, '_blank').focus();
            };

            return (
              <div>
                {!isNew && data && (
                  <Button
                    className="px-3"
                    style={{ minWidth: '160px', marginRight: '0.8rem' }}
                    disabled={dirty || loading}
                    variant="success"
                    onClick={() => printPdf()}
                    size="sm"
                  >
                    <FaFilePdf className="mb-1" /> {t('Εκτύπωση Εντύπου Αναγνώρισης')}
                  </Button>
                )}
              </div>
            );
          }}
        >
          {isNew || data ? (
            <IdocFormFields
              isNew={isNew}
              updateRecord={updateRecord}
              refetch={refetch}
              data={data}
              initialNewRecData={initialNewRecData}
            />
          ) : (
            <div style={{ minHeight: '250px' }}>
              <Spinner animation="border" className="mb-1" />
              &nbsp; Παρακαλώ Περιμένετε...
            </div>
          )}
          {appStore.isPlatformOwner && <RowFieldTooltips data={data} />}
        </FormCard>
      </>
    );
  }
};
